// Acceso a datos de órdenes. Lecturas y escrituras siempre por RPC autorizada.
import type { OrderStatus } from '../domain/status.ts';
import type { RequirementTarget, VisitOutcome, WorkType } from '../domain/orders.ts';
import type { Client } from './clients.ts';
import { AppFailure, toAppError } from './errors.ts';
import { supabase } from './supabase.ts';

export interface OrderSummary {
  id: string;
  order_number: number;
  folio: string;
  request_date: string;
  client_id: string;
  customer_name: string;
  customer_identification: string;
  equipment: string;
  brand: string;
  model: string;
  reported_fault: string;
  status: OrderStatus;
  work_type: WorkType;
  assigned_to: string | null;
  assigned_name: string | null;
  /** Tiene un presupuesto no anulado (badge "PRESUP."). Siempre false para el técnico. */
  has_quote?: boolean;
  created_at: string;
  updated_at: string;
}

export interface SearchCursor {
  /** Grupo de orden en la lista de pendientes (Listo primero para el personal, al final para el técnico). */
  rank?: number;
  created_at: string;
  id: string;
}

export interface SearchPage {
  items: OrderSummary[];
  total: number;
  next_cursor: SearchCursor | null;
}

export interface SearchParams {
  query: string;
  status: OrderStatus | null;
  unassigned: boolean;
  /** Solo órdenes en curso: oculta Listo, Cerrado y Anulado. */
  open?: boolean;
  /**
   * Órdenes pendientes del panel: oculta Cerrado y Anulado. Las Listo van primero para el personal (las revisa
   * para cerrarlas u objetarlas) y al final para el técnico.
   */
  pending?: boolean;
  technician: string | null;
  /** Solo órdenes de este cliente (ficha del cliente). */
  client?: string | null;
}

export interface OrderDetail {
  id: string;
  order_number: number;
  folio: string;
  request_date: string;
  client_id: string;
  /** Datos actuales del cliente (todos los roles los reciben). */
  client: Client;
  equipment: string;
  brand: string;
  model: string;
  reported_fault: string;
  serial: string;
  work_type: WorkType;
  assigned_to: string | null;
  assigned_name: string | null;
  status: OrderStatus;
  diagnosis: string;
  solution: string;
  source_order_id: string | null;
  version: number;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  annulled_at: string | null;
  annulled_by_name: string | null;
  annulment_reason: string | null;
  /** Observaciones de la última devolución Listo → Ruta (se conservan como referencia). */
  review_notes: string | null;
  returned_at: string | null;
  returned_by_name: string | null;
  /** Visitas registradas con "Actualizar OS". */
  visit_count: number;
  closed_at: string | null;
  closed_by_name: string | null;
  /** Lugar de atención ('taller' o 'domicilio'); null en las órdenes anteriores. Ver domain/physicalCheck.ts. */
  service_location?: string | null;
  /** Checklist del estado físico, si ya se registró. */
  physical_check?: unknown;
  /** Estado del equipo y accesorios que se reciben en el taller, en texto libre. */
  reception_notes?: string | null;
}

export interface HistoryEntry {
  id: string;
  action: string;
  actor_name: string;
  changes: Record<string, { before?: unknown; after?: unknown }>;
  reason: string | null;
  created_at: string;
}

export interface Technician {
  id: string;
  name: string;
  active: boolean;
}

function fail(error: unknown): never {
  throw new AppFailure(toAppError(error));
}

export async function searchOrders(params: SearchParams, cursor: SearchCursor | null, limit = 20): Promise<SearchPage> {
  const query = params.query.trim();
  const { data, error } = await supabase.rpc('search_orders', {
    p_unassigned: params.unassigned,
    ...(params.open ? { p_open: true } : {}),
    ...(params.pending ? { p_pending: true } : {}),
    p_limit: limit,
    ...(query !== '' ? { p_query: query } : {}),
    ...(params.status ? { p_status: params.status } : {}),
    ...(params.technician ? { p_technician: params.technician } : {}),
    ...(params.client ? { p_client: params.client } : {}),
    ...(cursor ? { p_cursor_created_at: cursor.created_at, p_cursor_id: cursor.id } : {}),
    ...(cursor?.rank != null ? { p_cursor_rank: cursor.rank } : {}),
  });
  if (error) fail(error);
  return data as unknown as SearchPage;
}

export async function getOrderDetail(orderId: string): Promise<OrderDetail> {
  const { data, error } = await supabase.rpc('get_order_detail', { p_order_id: orderId });
  if (error) fail(error);
  return data as unknown as OrderDetail;
}

export async function saveOrder(
  orderId: string | null,
  fields: Record<string, unknown>,
  expectedVersion: number | null,
): Promise<OrderDetail> {
  const { data, error } = await supabase.rpc('save_order', {
    p_order_id: orderId as string,
    p_fields: fields as never,
    p_expected_version: expectedVersion as number,
  });
  if (error) fail(error);
  return data as unknown as OrderDetail;
}

export async function transitionOrder(orderId: string, target: OrderStatus, expectedVersion: number, reason?: string) {
  const { data, error } = await supabase.rpc('transition_order', {
    p_order_id: orderId,
    p_target: target,
    p_expected_version: expectedVersion,
    ...(reason ? { p_reason: reason } : {}),
  });
  if (error) fail(error);
  return data as unknown as OrderDetail;
}

/** Guarda el diagnóstico de "Actualizar OS" y pasa la orden a Ruta en la misma transacción. */
export async function startVisit(orderId: string, fields: Record<string, unknown>, expectedVersion: number) {
  const { data, error } = await supabase.rpc('start_visit', {
    p_order_id: orderId,
    p_fields: fields as never,
    p_expected_version: expectedVersion,
  });
  if (error) fail(error);
  return data as unknown as OrderDetail;
}

/** Registra una visita y, si el resultado lo pide, cambia el estado en la misma transacción. */
export async function registerVisit(orderId: string, outcome: VisitOutcome, expectedVersion: number) {
  const { data, error } = await supabase.rpc('register_visit', {
    p_order_id: orderId,
    p_outcome: outcome,
    p_expected_version: expectedVersion,
  });
  if (error) fail(error);
  return data as unknown as OrderDetail;
}

export async function getRequirements(orderId: string, target: RequirementTarget): Promise<string[]> {
  const { data, error } = await supabase.rpc('get_order_requirements', { p_order_id: orderId, p_target: target });
  if (error) fail(error);
  return ((data as { missing?: string[] } | null)?.missing ?? []) as string[];
}

export async function getHistory(
  orderId: string,
  before?: { created_at: string; id: string },
): Promise<HistoryEntry[]> {
  const { data, error } = await supabase.rpc('get_order_history', {
    p_order_id: orderId,
    p_limit: 50,
    ...(before ? { p_before_created_at: before.created_at, p_before_id: before.id } : {}),
  });
  if (error) fail(error);
  return (data ?? []) as unknown as HistoryEntry[];
}

export async function listTechnicians(): Promise<Technician[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, active')
    .eq('role', 'technician')
    .order('name');
  if (error) fail(error);
  return data;
}
